import { IonSpinner } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSurveyJSFormValues } from "shared/components/survey-js-form/forms/useSurveyJSFormValues";
import SurveyJSFormComponent from "shared/components/survey-js-form/SurveyJSFormComponent";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { getPlatform } from "shared/types/DeviceInfo";
import { type HubFormPageRouteParams } from "shared/types/HubPageRouteParams";
import { ENV } from "hub/src/appConstants/env";
import LoadingPage from "hub/src/components/common/LoadingPage";
import PageComponent from "hub/src/components/common/PageComponent";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import { type HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";

const FormPage = ({
  routeParams: formPageRouteParams,
}: HubRouteConfig<HubFormPageRouteParams>) => {
  const device = useDeviceInfo();
  const session = useCustomSession();
  const router = useCustomRouter();
  const queryClient = useQueryClient();

  const formValues = useSurveyJSFormValues({
    ...formPageRouteParams,
    formId: formPageRouteParams?.formId ?? undefined,
    workflowInstanceId: formPageRouteParams?.workflowInstanceId ?? undefined,
    workflowId: formPageRouteParams!.workflowId,
    profile: session.profile,
    defaultPayload:
      formPageRouteParams?.defaultPayload ??
      session.profile?.additionalInformation,
    onCompleteRefreshCallback: async () => {
      await Promise.all(
        formValues.workflow?.conference?.map((c) =>
          queryClient.invalidateQueries({
            queryKey: [
              "conferenceRegistration",
              c.conferenceId,
              session.profile?.profileId,
            ],
          }),
        ) ?? [],
      );
      await session.refreshSession();
    },
    onCompleteCallback: async ({ workflowInstanceStopped }) => {
      if (workflowInstanceStopped.userhubRedirect) {
        if (workflowInstanceStopped.redirectWorkflowInstanceId) {
          workflowInstanceStopped.workflowInstanceId =
            workflowInstanceStopped.redirectWorkflowInstanceId;
        }
        if (workflowInstanceStopped.redirectWorkflowId) {
          workflowInstanceStopped.workflowId =
            workflowInstanceStopped.redirectWorkflowId;
        }

        router.removeFromState("formId");
        router.removeFromState("workflowId");
        router.removeFromState("workflowInstanceId");
        router.removeFromState("userhubRedirect");
        router.push(workflowInstanceStopped.userhubRedirect, {
          formId: null,
          workflowId: null,
          ...workflowInstanceStopped,
          // Don't leave this run's redirect in route state, or the next
          // form page would send it as its own userhubRedirect
          userhubRedirect: null,
        });
      }
    },
  });

  if (
    formValues.isLoading ||
    !formValues.form?.formDesign ||
    !formValues.formData
  ) {
    return <LoadingPage />;
  }

  return (
    <PageComponent>
      {device.isNativeApp && (
        <PageComponent.Header showBackButton={true}></PageComponent.Header>
      )}
      <PageComponent.Content>
        <SurveyJSFormComponent
          baseUrl={ENV.REACT_APP_PILLAR_API_URL}
          overrideTitle={formValues.overrideTitle}
          workflowInstance={formValues.workflowInstance}
          overrideCompleteButtonText={formValues.overrideCompleteButtonText}
          overrideDescription={formValues.overrideDescription}
          formDesign={formValues.form.formDesign}
          formData={formValues.formData}
          onComplete={formValues.onComplete}
          onCompleting={formValues.onCompleting}
          deviceType={getPlatform(device)}
          spinner={<IonSpinner />}
        />
        <formValues.SurveyJSAdminOnCompletingModal />
      </PageComponent.Content>
    </PageComponent>
  );
};

export default FormPage;
